// boardSelectors.ts
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

// Basic selectors
export const selectBoardState = (state: RootState) => state.board;
export const selectSelectedItem = (state: RootState) =>
  state.board.selectedItem;
export const selectViewType = (state: RootState) => state.board.viewType;
export const selectColumns = (state: RootState) => state.board.columns;
export const selectColumnOrder = (state: RootState) => state.board.columnOrder;
export const selectSearchQuery = (state: RootState) => state.board.searchQuery;
export const selectDebouncedSearchQuery = (state: RootState) =>
  state.board.debouncedSearchQuery;

// Filter selectors
export const selectFilters = createSelector([selectBoardState], (board) => ({
  selectedTags: board.selectedTags,
  selectedAvatars: board.selectedAvatars,
  selectedStatuses: board.selectedStatuses,
  selectedPriorities: board.selectedPriorities,
  selectedProjects: board.selectedProjects,
  selectedDueDate: board.selectedDueDate,
  selectedUpdatedDate: board.selectedUpdatedDate,
  dueDateStart: board.dueDateStart,
  dueDateEnd: board.dueDateEnd,
  updatedAtStart: board.updatedAtStart,
  updatedAtEnd: board.updatedAtEnd,
  selectedStartDate: board.selectedStartDate,
  startDateEnd: board.startDateEnd,
  startDateStart: board.startDateStart,
  isRecurring: board.isRecurring,
  selectedCreatedBy: board.selectedCreatedBy,
}));

// Grouping selectors
export const selectGroupingConfig = createSelector(
  [selectBoardState],
  (board) => ({
    groupBy: board.groupBy,
    subGroupBy: board.subGroupBy,
    sortOrder: board.sortOrder,
    showGroupOptions: board.showGroupOptions,
  })
);

// API data selectors
export const selectApiData = createSelector([selectBoardState], (board) => ({
  statusList: board.statusList,
  projectDetails: board.projectDetails,
  listOfBugs: board.listOfBugs,
  tagList: board.tagList,
  companyMemberData: board.companyMemberData,
  projects: board.projects,
  listCount: board.listCount,
}));

// Loading selectors
export const selectLoadingStates = createSelector(
  [selectBoardState],
  (board) => ({
    loadingStatuses: board.loadingStatuses,
    initialLoading: board.initialLoading,
    isBugsLoading: board.isBugsLoading,
    isBugsFetching: board.isBugsFetching,
    isProjectLoading: board.isProjectLoading,
    tagListLoading: board.tagListLoading,
  })
);

// **MISSING SELECTORS - ADD THESE:**

// Sub-group data selectors
export const selectSubGroupData = (state: RootState) =>
  state.board.subGroupData;

// Pagination selectors
export const selectPagination = (state: RootState) => state.board.pagination;
export const selectHasMoreMap = (state: RootState) => state.board.hasMoreMap;

// Loading status selectors
export const selectLoadingStatuses = (state: RootState) =>
  state.board.loadingStatuses;

// UI state selectors
export const selectMinimizedColumns = (state: RootState) =>
  state.board.minimizedColumns;
export const selectExpandedDetailPanels = (state: RootState) =>
  state.board.expandedDetailPanels;

// Form state selectors
export const selectIsAddingColumn = (state: RootState) =>
  state.board.isAddingColumn;
export const selectNewColumnTitle = (state: RootState) =>
  state.board.newColumnTitle;
export const selectIsDeleteConfirmOpen = (state: RootState) =>
  state.board.isDeleteConfirmOpen;
export const selectColumnToDelete = (state: RootState) =>
  state.board.columnToDelete;

// Technical state selectors
export const selectIsDragging = (state: RootState) => state.board.isDragging;
export const selectScrollPositions = (state: RootState) =>
  state.board.scrollPositions;

// Individual loading state selectors
export const selectInitialLoading = (state: RootState) =>
  state.board.initialLoading;
export const selectIsBugsLoading = (state: RootState) =>
  state.board.isBugsLoading;
export const selectIsBugsFetching = (state: RootState) =>
  state.board.isBugsFetching;
export const selectIsStatusLoading = (state: RootState) =>
  state.board.isStatusLoading;
export const selectIsProjectLoading = (state: RootState) =>
  state.board.isProjectLoading;
export const selectTagListLoading = (state: RootState) =>
  state.board.tagListLoading;

// Drawer state selector
export const selectDrawerOpen = (state: RootState) => state.board.drawerOpen;

// Enhanced loading states selector
export const selectLoadingStatesDetailed = createSelector(
  [selectBoardState],
  (board) => ({
    loadingStatuses: board.loadingStatuses,
    initialLoading: board.initialLoading,
    isBugsLoading: board.isBugsLoading,
    isBugsFetching: board.isBugsFetching,
    isStatusLoading: board.isStatusLoading,
    isProjectLoading: board.isProjectLoading,
    tagListLoading: board.tagListLoading,
  })
);

// UI state selector
export const selectUIState = createSelector([selectBoardState], (board) => ({
  drawerOpen: board.drawerOpen,
  minimizedColumns: board.minimizedColumns,
  expandedDetailPanels: board.expandedDetailPanels,
  isDragging: board.isDragging,
  scrollPositions: board.scrollPositions,
}));

// Form state selector
export const selectFormState = createSelector([selectBoardState], (board) => ({
  isAddingColumn: board.isAddingColumn,
  newColumnTitle: board.newColumnTitle,
  isDeleteConfirmOpen: board.isDeleteConfirmOpen,
  columnToDelete: board.columnToDelete,
}));

// Pagination state selector
export const selectPaginationState = createSelector(
  [selectBoardState],
  (board) => ({
    pagination: board.pagination,
    hasMoreMap: board.hasMoreMap,
  })
);

// Board data selector
export const selectBoardData = createSelector([selectBoardState], (board) => ({
  columns: board.columns,
  columnOrder: board.columnOrder,
  subGroupData: board.subGroupData,
  groupingData: board.groupingData,
  subGroupingData: board.subGroupingData,
}));

// Filtered columns based on current filters
export const selectFilteredColumns = createSelector(
  [selectColumns, selectFilters, selectGroupingConfig],
  (columns, filters, grouping) => {
    // Apply filtering logic based on current filters
    const filtered = { ...columns };

    // Add your filtering logic here based on the current filters

    return filtered;
  }
);

// Add these selectors to prevent re-computation issues
export const selectGroupingDataMemoized = createSelector(
  [selectBoardState],
  (board) => board.groupingData
);

export const selectSubGroupingDataMemoized = createSelector(
  [selectBoardState],
  (board) => board.subGroupingData
);

// Add selector for determining if grouping data is ready
export const selectIsGroupingDataReady = createSelector(
  [selectGroupingConfig, selectGroupingDataMemoized],
  (config, groupingData) => {
    if (config.groupBy === "None") return true;
    return Object.keys(groupingData).length > 0;
  }
);

// Add selector for determining if sub-grouping data is ready
export const selectIsSubGroupingDataReady = createSelector(
  [selectGroupingConfig, selectSubGroupingDataMemoized],
  (config, subGroupingData) => {
    if (config.subGroupBy === "None") return true;
    return Object.keys(subGroupingData).length > 0;
  }
);

// Check if any filters are active - Updated to include project filter
export const selectHasActiveFilters = createSelector(
  [selectFilters],
  (filters) => {
    return (
      filters.selectedTags.length > 0 ||
      filters.selectedAvatars.length > 0 ||
      filters.selectedStatuses.length > 0 ||
      filters.selectedPriorities.length > 0 ||
      filters.selectedProjects.length > 0 || // Add selectedProjects check
      filters.selectedDueDate !== "" ||
      filters.selectedUpdatedDate !== "" ||
      filters.selectedStartDate !== "" ||
      filters.selectedCreatedBy.length > 0
    );
  }
);

// **ADDITIONAL COMPUTED SELECTORS:**

// Selector to get visible columns (non-hidden and matching filters) - Updated to include project filter
export const selectVisibleColumns = createSelector(
  [selectColumns, selectFilters, selectGroupingConfig],
  (columns, filters, grouping) => {
    const { groupBy } = grouping;
    const {
      selectedStatuses,
      selectedAvatars,
      selectedPriorities,
      selectedTags,
      selectedProjects, // Add selectedProjects
    } = filters;

    return Object.keys(columns).filter((columnId) => {
      const column = columns[columnId];

      if (column?.hidden) {
        return false;
      }

      // Apply grouping-specific filters
      if (groupBy === "Status" && selectedStatuses.length > 0) {
        return selectedStatuses.includes(columnId);
      }

      if (groupBy === "Assignee" && selectedAvatars.length > 0) {
        return selectedAvatars.includes(column?.id);
      }

      if (groupBy === "Priority" && selectedPriorities.length > 0) {
        return selectedPriorities.includes(column?.title);
      }

      if (groupBy === "Tags" && selectedTags.length > 0) {
        return selectedTags.includes(column?.title);
      }

      // Add project filter logic
      if (groupBy === "Project" && selectedProjects.length > 0) {
        return selectedProjects.includes(columnId);
      }

      return true;
    });
  }
);

// Selector to get column with tasks count
export const selectColumnsWithTaskCount = createSelector(
  [selectColumns],
  (columns) => {
    return Object.keys(columns).reduce((acc, columnId) => {
      const column = columns[columnId];
      acc[columnId] = {
        ...column,
        taskCount: column.tasks?.length || 0,
      };
      return acc;
    }, {} as Record<string, any>);
  }
);

// Selector to determine if board is in loading state
export const selectIsBoardLoading = createSelector(
  [selectLoadingStatesDetailed],
  (loadingStates) => {
    return (
      loadingStates.initialLoading ||
      loadingStates.isBugsLoading ||
      loadingStates.isStatusLoading ||
      loadingStates.isProjectLoading
    );
  }
);

// Selector to get all tasks across all columns
export const selectAllTasks = createSelector([selectColumns], (columns) => {
  const allTasks: any[] = [];
  Object.values(columns).forEach((column) => {
    if (column.tasks) {
      allTasks.push(...column.tasks);
    }
  });
  return allTasks;
});

// Selector to get total task count
export const selectTotalTaskCount = createSelector(
  [selectAllTasks],
  (allTasks) => allTasks.length
);

// Selector to check if sub-grouping is active
export const selectIsSubGroupingActive = createSelector(
  [selectGroupingConfig],
  (config) => config.subGroupBy !== "None"
);

// Selector to check if any column is being loaded
export const selectIsAnyColumnLoading = createSelector(
  [selectLoadingStatuses],
  (loadingStatuses) => {
    return Object.values(loadingStatuses).some(
      (status: any) => status.isInitialLoading || status.isLoadingMore
    );
  }
);

// Selector to get loading status for a specific column
export const selectColumnLoadingStatus = createSelector(
  [selectLoadingStatuses, (state: RootState, columnId: string) => columnId],
  (loadingStatuses, columnId) => {
    return (
      loadingStatuses[columnId] || {
        isInitialLoading: false,
        isLoadingMore: false,
      }
    );
  }
);

// Selector to get pagination info for a specific column
export const selectColumnPagination = createSelector(
  [selectPagination, (state: RootState, columnId: string) => columnId],
  (pagination, columnId) => {
    return (
      pagination[columnId] || {
        page: 1,
        limit: 10,
        total: 0,
      }
    );
  }
);

// Selector to check if a column has more data to load
export const selectColumnHasMore = createSelector(
  [selectHasMoreMap, (state: RootState, columnId: string) => columnId],
  (hasMoreMap, columnId) => {
    return hasMoreMap[columnId] || false;
  }
);

// Selector for current search and filter state
export const selectSearchAndFilterState = createSelector(
  [
    selectSearchQuery,
    selectDebouncedSearchQuery,
    selectFilters,
    selectHasActiveFilters,
  ],
  (searchQuery, debouncedSearchQuery, filters, hasActiveFilters) => ({
    searchQuery,
    debouncedSearchQuery,
    filters,
    hasActiveFilters,
  })
);

// Selector to get sub-group data for a specific sub-group
export const selectSubGroupDataById = createSelector(
  [selectSubGroupData, (state: RootState, subGroupId: string) => subGroupId],
  (subGroupData, subGroupId) => {
    return subGroupData[subGroupId] || null;
  }
);

// Selector to check if a column is minimized
export const selectIsColumnMinimized = createSelector(
  [selectMinimizedColumns, (state: RootState, columnId: string) => columnId],
  (minimizedColumns, columnId) => {
    return minimizedColumns[columnId] || false;
  }
);

// Selector to get scroll position for a column
export const selectColumnScrollPosition = createSelector(
  [selectScrollPositions, (state: RootState, columnId: string) => columnId],
  (scrollPositions, columnId) => {
    return scrollPositions[columnId] || 0;
  }
);

export const selectActiveSprintIds = createSelector(
  [selectBoardState],
  (board) => board.activeSprintIds
);

export const selectActiveSprints = createSelector(
  [selectBoardState],
  (board) => board.activeSprints
);

export const selectBacklogViewType = createSelector(
  [selectBoardState],
  (board) => board.backlogViewType
);
